import React, { useState, useRef, useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useOverlayPortalContainer } from 'modules/Common/utils/overlayHost';

/**
 * CgPopover is a component that displays a popover with custom content
 * when the trigger element is hovered over or clicked, depending on the trigger type.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {React.ReactElement} props.triggerElement - The element that triggers the popover when interacted with.
 * @param {React.ReactNode} props.popoverContent - The content to be displayed inside the popover.
 * @param {string} [props.popoverTitle] - An optional title to display at the top of the popover.
 * @param {string} [props.position="bottom"] - The position of the popover relative to the trigger element. Can be "top", "right", "bottom", or "left".
 * @param {'start'|'center'|'end'} [props.align="center"] - Alignment of the popover along the trigger edge.
 * @param {string} [props.trigger="hover"] - The event that triggers the popover. Can be "hover" or "click".
 * @param {string} [props.popoverContentClassName=""] - Additional CSS classes to apply to the popover content.
 * @param {string} [props.className=""] - Additional CSS classes to apply to the popover container.
 * @param {number} [props.openDelay=0] - Delay in ms before opening the popover.
 * @param {number} [props.closeDelay=0] - Delay in ms before closing the popover.
 * @param {number} [props.sideOffset=5] - Offset from the trigger element in pixels.
 * @param {number} [props.mainAxisOffset=0] - Offset on the main axis (vertical for top/bottom, horizontal for left/right).
 * @param {number|object} [props.collisionPadding=8] - Padding from viewport edges when avoiding collisions.
 * @param {boolean} [props.avoidCollisions=true] - Enable automatic repositioning to keep content in viewport.
 * @param {('partial'|'always') } [props.sticky='partial'] - Stick to edges when space is constrained.
 * @param {string} [props.maxHeight='max-h-[70vh]'] - Tailwind class controlling max height of content.
 * @param {boolean} [props.open] - Controlled open state. When provided, the component becomes controlled.
 * @param {function} [props.onOpenChange] - Callback when open state changes. Required when using controlled mode.
 * @param {function} [props.onCloseAutoFocus] - Radix close-focus handler; preventDefault keeps focus off the trigger.
 * @returns {JSX.Element} The rendered popover component.
 */
const CgPopover = ({
  triggerElement,
  popoverContent,
  popoverTitle,
  position = 'bottom',
  align = 'center',
  trigger = 'hover',
  popoverContentClassName = '',
  popoverContentStyle,
  // Border classes go through their own prop so callers can swap the
  // built-in `border border-gray-200` for something else (e.g. just
  // `border-2` to set width without color) and then drive color via
  // popoverContentStyle. Avoids the Tailwind `border-gray-200` class
  // competing with an inline borderColor override.
  popoverContentBorderClassName = 'border border-gray-200',
  // Style spread onto the Radix `Popover.Arrow` SVG. The `fill` (default
  // white) and any `stroke` set here apply to the inner polygon via
  // SVG presentation-attribute inheritance, so callers can give the
  // arrow a colored outline to match the popover's frame.
  popoverArrowStyle,
  className = '',
  openDelay = 0,
  closeDelay = 300,
  sideOffset = 5,
  mainAxisOffset = 0,
  maxWidth,
  matchTriggerWidth = false,
  collisionPadding = 8,
  avoidCollisions = true,
  sticky = 'partial',
  maxHeight = 'max-h-[70vh]',
  open: controlledOpen,
  onOpenChange,
  anchorOnly = false,
  onCloseAutoFocus,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const portalContainer = useOverlayPortalContainer();

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? onOpenChange : setInternalOpen;
  const openTimeoutRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const contentRef = useRef(null);

  // Extract pixel value from Tailwind class (e.g., "max-w-[450px]" -> "450px")
  const extractMaxWidth = (tailwindClass) => {
    const match = tailwindClass?.match(/max-w-\[(.+?)\]/);
    return match ? match[1] : null; // Return null if no match
  };

  const maxWidthValue = extractMaxWidth(maxWidth);

  // Map position prop to Radix side
  const getRadixSide = (position) => {
    switch (position) {
      case 'top':
        return 'top';
      case 'right':
        return 'right';
      case 'left':
        return 'left';
      case 'bottom':
      default:
        return 'bottom';
    }
  };

  // Clear any pending timeouts
  const clearTimeouts = () => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  // Handle mouse enter on trigger
  const handleMouseEnter = () => {
    if (trigger === 'hover') {
      clearTimeouts();
      if (openDelay > 0) {
        openTimeoutRef.current = setTimeout(() => {
          setOpen(true);
        }, openDelay);
      } else {
        setOpen(true);
      }
    }
  };

  // Handle mouse leave from trigger
  const handleMouseLeave = () => {
    if (trigger === 'hover') {
      clearTimeouts();
      // Use a longer delay to allow time to move to popover content or child popovers
      const effectiveCloseDelay = Math.max(closeDelay, 150);
      closeTimeoutRef.current = setTimeout(() => {
        setOpen(false);
      }, effectiveCloseDelay);
    }
  };

  // Handle mouse enter on popover content
  const handleContentMouseEnter = () => {
    if (trigger === 'hover') {
      // Clear any pending close timeout when hovering over content
      clearTimeouts();
    }
  };

  // Handle mouse leave from popover content
  const handleContentMouseLeave = () => {
    if (trigger === 'hover') {
      clearTimeouts();
      // Use a longer delay to allow time to move to child popovers
      const effectiveCloseDelay = Math.max(closeDelay, 150);
      closeTimeoutRef.current = setTimeout(() => {
        setOpen(false);
      }, effectiveCloseDelay);
    }
  };

  // Handle click trigger
  const handleClick = () => {
    if (trigger === 'click') {
      setOpen(!open);
    }
  };

  // Clean up timeouts on unmount
  React.useEffect(() => {
    return () => {
      clearTimeouts();
    };
  }, []);

  // When a click-triggered popover is open inside a modal, prevent the modal's
  // enforceFocus from stealing focus away from inputs inside the popover portal.
  // The modal (@restart/ui) listens for `focus` on `document` in capture phase.
  // By listening on `window` in capture phase, we fire first (window → document)
  // and can stop propagation before the modal's handler schedules its refocus.
  useEffect(() => {
    if (trigger !== 'click' || !open) return;
    if (!document.querySelector('.modal.show')) return;

    const handleFocus = (event) => {
      if (
        contentRef.current &&
        event.target instanceof Node &&
        contentRef.current.contains(event.target)
      ) {
        event.stopPropagation();
      }
    };

    window.addEventListener('focus', handleFocus, true);
    return () => {
      window.removeEventListener('focus', handleFocus, true);
    };
  }, [trigger, open]);

  const triggerProps = {
    ...(trigger === 'hover' && {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    }),
    ...(trigger === 'click' && {
      onClick: handleClick,
    }),
  };

  const TriggerPart = anchorOnly ? Popover.Anchor : Popover.Trigger;

  return (
    <Popover.Root
      open={open}
      onOpenChange={setOpen}
    >
      <TriggerPart asChild>{React.cloneElement(triggerElement, triggerProps)}</TriggerPart>

      <Popover.Portal container={portalContainer}>
        <Popover.Content
          ref={contentRef}
          data-cg-escape-closes={trigger === 'hover' ? undefined : ''}
          onEscapeKeyDown={() => setOpen(false)}
          className={`
            inline-block text-sm text-gray-500 bg-white ${popoverContentBorderClassName}
            rounded-lg shadow-sm z-[99999] break-words whitespace-normal
            [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]
            data-[state=open]:animate-in data-[state=closed]:animate-out
            data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
            data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95
            data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2
            data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2
            duration-200 ease-out
            ${popoverContentClassName}
          `
            .replace(/\s+/g, ' ')
            .trim()}
          style={{
            zIndex: 99999,
            // When matchTriggerWidth is true, stretch to exactly the trigger's width.
            // Radix sets --radix-popover-trigger-width automatically on the content element.
            ...(matchTriggerWidth && { width: 'var(--radix-popover-trigger-width)' }),
            // Ensure the popover never overflows the viewport by constraining to available space
            // Radix exposes --radix-popper-available-width/height which accounts for collisions
            maxWidth: matchTriggerWidth
              ? undefined
              : maxWidthValue
                ? `min(${maxWidthValue}, var(--radix-popper-available-width), calc(100vw - 16px))`
                : 'min(var(--radix-popper-available-width), calc(100vw - 16px))',
            maxHeight: 'min(var(--radix-popper-available-height), calc(100vh - 16px))',
            overflow: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            // Inline style override lets callers tune border color / shadow
            // etc. on a per-popover basis. Comes last so it wins over the
            // built-in style fields above when keys overlap (e.g.
            // CgDiagramPopover sets borderColor to mirror the node color).
            ...popoverContentStyle,
          }}
          side={getRadixSide(position)}
          align={align}
          sideOffset={sideOffset}
          alignOffset={mainAxisOffset}
          avoidCollisions={avoidCollisions}
          collisionPadding={collisionPadding}
          sticky={sticky}
          updatePositionStrategy="always"
          onMouseEnter={trigger === 'hover' ? handleContentMouseEnter : undefined}
          onMouseLeave={trigger === 'hover' ? handleContentMouseLeave : undefined}
          onOpenAutoFocus={(event) => {
            // Always prevent Radix from stealing focus on open.
            // For hover: maintains natural mouse behavior.
            // For click: avoids conflict with parent focus-trapping containers
            // (e.g., react-bootstrap Modal's enforceFocus).
            event.preventDefault();
          }}
          onCloseAutoFocus={
            // A hover never took the focus, so closing has none to give back.
            onCloseAutoFocus ??
            (trigger === 'hover' ? (event) => event.preventDefault() : undefined)
          }
          onFocusOutside={(event) => {
            // When inside a modal, the modal's enforceFocus can steal focus from the
            // popover portal, causing an unwanted dismiss. Prevent this.
            if (trigger === 'click' && document.querySelector('.modal.show')) {
              event.preventDefault();
            }
          }}
        >
          {popoverTitle && (
            <div className="px-3 py-2 bg-white border-b border-gray-200 rounded-t-lg">
              <h3 className="font-semibold text-gray-900">{popoverTitle}</h3>
            </div>
          )}
          <div className="px-3 py-2">{popoverContent}</div>
          <Popover.Arrow
            className="fill-white"
            style={popoverArrowStyle}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

/**
 * Determines the popover's position relative to the trigger element.
 *
 * @param {string} position - The position of the popover ("top", "right", "bottom", "left").
 * @param {number} offset - The offset from the trigger element.
 * @returns {string} The CSS classes to position the popover.
 */
const getPopoverPosition = (position, offset) => {
  const offsetClass = `${offset}px`;
  switch (position) {
    case 'top':
      return `bottom-full mb-[${offsetClass}]`;
    case 'right':
      return `left-full ml-[${offsetClass}]`;
    case 'left':
      return `right-full mr-[${offsetClass}] mt-40`; // Adjusted to move the popover down
    case 'bottom':
    default:
      return `top-full mt-[${offsetClass}]`;
  }
};

/**
 * Aligns the popover with respect to the trigger element's position.
 *
 * @param {string} position - The position of the popover ("top", "right", "bottom", "left").
 * @returns {string} The CSS classes to align the popover.
 */
const getPopoverAlignment = (position) => {
  switch (position) {
    case 'top':
    case 'bottom':
      return 'left-1/2 transform -translate-x-1/2';
    case 'left':
    case 'right':
      return 'top-1/2 transform -translate-y-1/2';
    default:
      return '';
  }
};

/**
 * Determines the position of the popover's arrow based on the popover's position.
 *
 * @param {string} position - The position of the popover ("top", "right", "bottom", "left").
 * @returns {string} The CSS classes to position the popover's arrow.
 */
const getPopoverArrowPosition = (position) => {
  switch (position) {
    case 'top':
      return 'left-1/2 transform -translate-x-1/2 bottom-[-6px] border-b border-r border-gray-200';
    case 'right':
      return 'top-1/2 transform -translate-y-1/2 left-[-6px] border-b border-l border-gray-200';
    case 'left':
      return 'top-[19%] transform -translate-y-1/2 right-[-6px] border-t border-r border-gray-200';
    case 'bottom':
    default:
      return 'left-1/2 transform -translate-x-1/2 top-[-6px] border-t border-l border-gray-200';
  }
};

export default CgPopover;
